import React from 'react'

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ClientScenario } from '@cash-web/protos/squareup/franklin/api/client_scenario.pb'
import { RegisterEmailResponseStatus } from '@cash-web/protos/squareup/franklin/app/email.pb'
import { EmailVerificationBlocker } from '@cash-web/protos/squareup/franklin/blockers/email_blockers.pb'
import { EmailVerificationBlockerSupplement } from '@cash-web/protos/squareup/franklin/scenarios.pb'
import { optionalScopeHeaders } from '@cash-web/shared-blocker-data-access-api'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationRegisterEmail } from '@cash-web/shared-data-access-api/react-query/useMutationRegisterEmail'
import { useMutationVerifyEmail } from '@cash-web/shared-data-access-api/react-query/useMutationVerifyEmail'
import { useFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { ConfirmationCodeInput } from '@cash-web/shared-feature-form'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { getRegisterEmailErrors, getStatusError, getVerifyEmailErrors } from '../../util/statusErrors'
import { BaseBlocker, BaseBlockerFormFields, BlockerProps, HelpModalItem } from '../'

interface FormFields extends BaseBlockerFormFields {
  code: string
}

interface Props extends BlockerProps {
  blocker: EmailVerificationBlocker
  supplement?: EmailVerificationBlockerSupplement
}

export function BlockerEmailVerification({ blocker, supplement, skippable }: Props): React.ReactElement {
  const { t } = useTranslation()
  const form = useForm<FormFields>({ mode: 'onTouched' })
  const flow = useFlowContext()
  const canShowAdditionalHelpItems = useFeatureFlag('cash-web-account-verify-alias-additional-help-items')

  // We don't need to explicitly pass clientScenario and flowToken as they're handled by middleware
  const { mutateAsync: registerEmail } = useMutationRegisterEmail({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
      optionalRequestHeaders: optionalScopeHeaders(),
    },
  })

  const { mutateAsync: verifyEmail } = useMutationVerifyEmail({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
      optionalRequestHeaders: optionalScopeHeaders(),
    },
  })

  const canResendCode = useFeatureFlag('cash-frontend-product-resend-code')
  const { handleBlockerResponse, genericError } = useBlockerResponseHandler()

  const resendCode = async () => {
    try {
      const response = await registerEmail({
        email_address: blocker.email,
        payment_tokens: flow.active?.paymentTokens,
      })
      if (response.status === RegisterEmailResponseStatus.SUCCESS) {
        return {
          success: true,
          message: t('A code has been sent to {alias}', {
            alias: blocker.email,
            context: "Success text. indicating that we've successfully made a voice call to the user's phone number.",
          }),
        }
      } else {
        return {
          success: false,
          message: getStatusError(getRegisterEmailErrors(genericError, t), response.status, genericError),
        }
      }
    } catch {
      return {
        success: false,
        message: t('Oops, something went wrong. Please refresh the page and try again.', {
          context: 'Error text. unknown error',
        }),
      }
    }
  }

  const editEmail = () => {
    flow.updateWithBlockerDescriptors({
      blockerDescriptors: [
        {
          blocker: {
            alias_blocker: {},
          },
        },
      ],
      queriesToInvalidate: [],
    })
    return Promise.resolve({ success: true })
  }

  const onSubmit = async ({ code }: FormFields) => {
    await handleBlockerResponse({
      promisedBlockerUpdate: verifyEmail({
        email_address: blocker.email,
        verification_code: code,
        payment_tokens: flow.active?.paymentTokens,
      }),
      errors: getVerifyEmailErrors(t, blocker.email),
    })
  }

  let helpItems: HelpModalItem[] = []
  const isEntryScreen = blocker.email && !blocker.email?.includes('*')

  if (isEntryScreen || canResendCode) {
    helpItems = [{ text: t('Resend code', { context: 'Button label. ' }), customAction: resendCode }]
  }

  // we only want to show edit email if the email is not redacted (only if they've entered it)
  if (isEntryScreen && flow.active?.clientScenario === ClientScenario.ONBOARDING) {
    helpItems = helpItems.concat({
      text: t('Edit email', { context: 'button CTA for a button that allows a user to edit their email' }),
      customAction: editEmail,
    })
  }

  if (supplement?.additional_help_items && canShowAdditionalHelpItems) {
    helpItems = helpItems.concat(supplement.additional_help_items)
  }

  return (
    <BaseBlocker
      mainText={t('Enter the code sent to your email', {
        context: 'Title text. The code is a series of digits and hyphens.',
      })}
      secondaryText={t('We sent the code to {email}.', {
        context: 'Subtitle text. The code is a series of digits and hyphens',
        email: blocker.email,
      })}
      form={form}
      onSubmit={onSubmit}
      helpModal={{
        helpItems,
        title: t('We sent you a code', { context: 'Title text. The code is a series of digits and hyphens' }),
        subtext: t(
          'Check your email for a code we sent to {email}. If you haven’t received it, we can send it again.',
          {
            context: 'Subtitle text. The code is a series of digits and hyphens',
            email: blocker.email,
          }
        ),
      }}
      skippable={skippable}
    >
      <ConfirmationCodeInput />
    </BaseBlocker>
  )
}
